import type {
  DocumentCategoryCount,
  DocumentDetail,
  DocumentRow as ApiRow,
} from '@/api/generated';
import type { StatusTone } from '@/components/ui/StatusChip';
import { sizeInWords, typeWord } from '@/lib/files';
import { formatCount } from '@/lib/numbers';

export { sizeInWords, typeWord };

/**
 * A document as the screens read it: the backend's row in camel case, with
 * the pending replacement present (null on a list row, which never carries
 * one). Mapped in one place so a backend rename is one edit.
 */

export type ScanStatus = 'PENDING' | 'CLEAN' | 'INFECTED' | 'FAILED';
export type Lifecycle = 'DRAFT' | 'PUBLISHED' | 'INACTIVE' | 'ARCHIVED';

export interface PendingFile {
  fileName: string;
  contentType: string;
  sizeBytes: number;
  uploadedAt: string;
}

export interface DocumentRow {
  id: string;
  title: string;
  description: string | null;
  category: { id: string; name: string } | null;
  contentType: string;
  fileName: string;
  sizeBytes: number;
  scanStatus: ScanStatus;
  lifecycle: Lifecycle;
  publishRequested: boolean;
  inArc: boolean;
  fileUploadedAt: string;
  publishedAt: string | null;
  updatedAt: string;
  views: number;
  downloads: number;
  pendingFile: PendingFile | null;
}

export function toDocumentRow(api: ApiRow | DocumentDetail): DocumentRow {
  const pending = 'pending_file' in api ? api.pending_file : null;
  return {
    id: api.id,
    title: api.title,
    description: api.description ?? null,
    category: api.category ? { id: api.category.id, name: api.category.name } : null,
    contentType: api.content_type,
    fileName: api.file_name,
    sizeBytes: api.size_bytes,
    scanStatus: api.scan_status as ScanStatus,
    lifecycle: api.lifecycle as Lifecycle,
    publishRequested: api.publish_requested,
    inArc: api.in_arc,
    fileUploadedAt: api.file_uploaded_at,
    publishedAt: api.published_at ?? null,
    updatedAt: api.updated_at,
    views: api.views,
    downloads: api.downloads,
    pendingFile: pending
      ? {
          fileName: pending.file_name,
          contentType: pending.content_type,
          sizeBytes: pending.size_bytes,
          uploadedAt: pending.uploaded_at,
        }
      : null,
  };
}

/** The lifecycle words, as the backend's API note asks them to be shown (P3). */
export const LIFECYCLE_WORDS: Record<Lifecycle, string> = {
  DRAFT: 'Draft',
  PUBLISHED: 'Published',
  INACTIVE: 'Unpublished',
  ARCHIVED: 'Removed',
};

/**
 * Whether a replacement is awaiting its scan. The detail and every write reply
 * carry it as `pendingFile`; a list row does not, and there PENDING on a
 * document that is already published or unpublished can only be a
 * replacement, because a first upload stays a draft until its scan is clean
 * (backend B7). A draft whose file was replaced is the one case a list row
 * cannot tell from a first scan; the drawer, reading the detail, can.
 */
export function replacementPending(row: DocumentRow): boolean {
  if (row.pendingFile !== null) return true;
  return (
    row.scanStatus === 'PENDING' &&
    (row.lifecycle === 'PUBLISHED' || row.lifecycle === 'INACTIVE')
  );
}

/** Whether the document's own first file is still being scanned. */
export function firstScanPending(row: DocumentRow): boolean {
  return (
    row.scanStatus === 'PENDING' && row.lifecycle === 'DRAFT' && !replacementPending(row)
  );
}

/**
 * Whether the current file has been cleared (backend D34): its own scan is
 * clean, or a replacement is pending, in which case `scanStatus` describes
 * the replacement and the current file was clean when it was accepted.
 */
export function hasCleanFile(row: DocumentRow): boolean {
  return row.scanStatus === 'CLEAN' || replacementPending(row);
}

/**
 * Whether the scan word describes the document itself rather than a
 * replacement: the first file is still scanning, or was refused. A removed
 * row is Removed whatever the scan said, except when the scan is why it was
 * removed.
 */
function scanDescribesTheDocument(row: DocumentRow): boolean {
  if (row.scanStatus === 'INFECTED' || row.scanStatus === 'FAILED') return true;
  return firstScanPending(row);
}

/** One word per row; a scan word wins over a lifecycle word (I1). */
export function stateWord(row: DocumentRow): string {
  if (scanDescribesTheDocument(row)) {
    if (row.scanStatus === 'INFECTED') return 'Rejected';
    if (row.scanStatus === 'FAILED') return 'Scan failed';
    return 'Scanning';
  }
  return LIFECYCLE_WORDS[row.lifecycle];
}

/** Colour reinforces the word; the word carries it. */
export function stateTone(row: DocumentRow): StatusTone {
  if (scanDescribesTheDocument(row)) {
    return row.scanStatus === 'PENDING' ? 'warning' : 'error';
  }
  return row.lifecycle === 'PUBLISHED' ? 'success' : 'neutral';
}

export function placementWord(inArc: boolean): string {
  return inArc ? 'In ARC' : 'Not in ARC';
}

/**
 * A row's views or downloads, written with the locale's separators (P15), or
 * null for a document never published, which the screens show as a dash
 * named "Never published" (P17). One published and since unpublished keeps
 * its numbers, 0 included.
 */
export function countOrNever(row: DocumentRow, n: number): string | null {
  return row.publishedAt === null ? null : formatCount(n);
}

/** One tab on the library page. */
export interface CategoryTab {
  /** A category id, `'all'` or `'uncategorized'`. */
  id: string;
  name: string;
  count: number;
}

export const ALL_TAB = 'all';
export const UNCATEGORIZED_TAB = 'uncategorized';

/**
 * The tab row from the reply's `category_counts` (I5): All Documents first
 * with the sum, the categories as the backend ordered them, Uncategorized
 * last. Nothing is computed from the page itself.
 */
export function tabsFrom(counts: DocumentCategoryCount[]): CategoryTab[] {
  const categories = counts.filter((count) => count.id);
  const uncategorized = counts.find((count) => !count.id);
  const total = counts.reduce((sum, count) => sum + count.count, 0);
  return [
    { id: ALL_TAB, name: 'All Documents', count: total },
    ...categories.map((count) => ({
      id: count.id as string,
      name: count.name,
      count: count.count,
    })),
    { id: UNCATEGORIZED_TAB, name: 'Uncategorized', count: uncategorized?.count ?? 0 },
  ];
}
